/**
 * Quick Add Manager - Moon Moon Agency
 * Modale "Achat rapide" (mm-quick-add-modal.liquid, chassis mm-panel variante
 * --dialog) : au changement de variante dans une modale, résout la variante
 * correspondante depuis le JSON déjà présent dans le DOM (pas de Section
 * Rendering API, pas de changement d'URL — perf) et met à jour localement le
 * prix, le média et l'état du CTA. Médias = mm-quick-add-media.liquid (léger,
 * volontairement distinct de mm-product-medias/PDP) : 1 seule instance Splide
 * par modale, montée à l'ouverture, vignettes = boutons qui appellent
 * splide.go(index). Le submit du form profite du handler générique déjà
 * existant window.initAddToCartForms (mm_cart.js) : ce module se contente
 * d'écouter cart:add-to-cart-success pour fermer la modale.
 *
 * Hydratation lazy (pattern éprouvé sur MM-Jolly-Mama-V.2) : la modale est
 * rendue dans un <template data-quick-add-template data-card-id="…"> par
 * mm-quick-add-modal.liquid, jamais directement dans le DOM. Le <template>
 * n'est pas dans la page : la section MM-Quick-Add le rend à la demande
 * (Section Rendering API), préchargé au survol / toucher du bouton +. Les cartes
 * produit vivent souvent dans un slider (Splide) dont le containment
 * (overflow/transform) piégerait le position:fixed de la modale si elle était
 * un descendant. Au premier clic sur [data-quick-add-open], on clone le
 * <template> correspondant sur document.body EN PHASE CAPTURE — donc avant
 * le listener 'click' de mm-panel.js (phase bulle, non modifiable) — pour que
 * l'élément existe dans le DOM au moment où mm-panel.js le cherche par id.
 * Ouvertures suivantes : déjà hydratée, no-op.
 */

/*********************************************************************************************
 * 🔧 QUICK ADD MANAGER
 */
const QuickAddManager = {
	config: {
		selectors: {
			dialog: '.mm-panel--dialog[data-quick-add]',
			openTrigger: '[data-quick-add-open]',
			template: '[data-quick-add-template]',
			mediaThumb: '[data-quick-add-media-thumb]',
			variantsJson: '[data-quick-add-variants-json]',
			pricesJson: '[data-quick-add-prices-json]',
			variantInput: '[data-variant-input]',
			hiddenIdInput: '[data-selected-variant-id]',
			mediaRoot: '[data-quick-add-media]',
			priceCurrent: '[data-price]',
			priceCompare: '.mm-market-price',
			priceCompareValue: '[data-market-price]',
			priceSaving: '[data-price-saving]',
			priceSavingText: '[data-price-saving-text] .mm-label-text',
			pricePercentage: '[data-price-percentage]',
			pricePercentageText: '[data-price-percentage-text] .mm-label-text',
			ctaRoot: '.mm-pcta',
			ctaPrice: '.mm-pcta .mm-btn__price',
			submitButton: '[type="submit"]',
			oosState: '[data-pcta-oos-state]',
			oosLink: '[data-pcta-oos-state] a',
			availableState: '[data-pcta-available-state]',
			variantValue: '[data-variant-value]',
			photoOption: '[data-variant-photo-radio]',
			pillOption: '[data-variant-pill-radio]',
		},
		classes: {
			mounted: 'mm-media-mounted',
			thumbActive: 'is-active',
			photoOos: 'mm-product-variant-photo-option--oos',
			pillOos: 'mm-product-variant-pills-option--oos',
		},
	},

	// Une instance Splide par modale (clé = id du conteneur mm-qadd-media),
	// montée à l'ouverture — pas de singleton global partagé (chaque modale
	// est indépendante, contrairement au chassis mm-product-medias de la PDP).
	_sliders: new Map(),
	_hydrated: new Set(),
	_requests: new Map(),

	init() {
		this._bindEvents();
		window.log('info', '🔧 [QUICK_ADD]', 'Initialized');
	},

	_bindEvents() {
		// Phase capture : s'exécute avant le listener 'click' de mm-panel.js
		// (phase bulle), donc la modale existe déjà dans le DOM quand ce
		// dernier cherche l'élément par son id.
		document.addEventListener('click', (e) => {
			const trigger = e.target.closest(this.config.selectors.openTrigger);
			if (!trigger) return;
			const cardId = trigger.dataset.cardId;
			if (this._hydrated.has(cardId) || this._findTemplate(cardId)) {
				this._hydrate(cardId);
				return;
			}
			e.preventDefault();
			e.stopImmediatePropagation();
			trigger.setAttribute('aria-busy', 'true');
			this._fetchTemplate(trigger).then(() => {
				trigger.removeAttribute('aria-busy');
				this._hydrate(cardId);
				if (this._hydrated.has(cardId)) trigger.click();
				else if (trigger.dataset.quickAddUrl) window.location.href = trigger.dataset.quickAddUrl;
			});
		}, { capture: true });

		['pointerover', 'touchstart', 'focusin'].forEach((type) => {
			document.addEventListener(type, (e) => {
				const trigger = e.target.closest && e.target.closest(this.config.selectors.openTrigger);
				if (trigger && !this._hydrated.has(trigger.dataset.cardId)) this._fetchTemplate(trigger);
			}, { passive: true });
		});

		document.addEventListener('click', (e) => {
			const trigger = e.target.closest(this.config.selectors.openTrigger);
			if (trigger) {
				const dialog = document.getElementById(trigger.getAttribute('aria-controls'));
				if (dialog) this._mountMedia(dialog);
				return;
			}

			const thumb = e.target.closest(this.config.selectors.mediaThumb);
			if (thumb) this._onThumbClick(thumb);
		});

		document.addEventListener('change', (e) => {
			const input = e.target.closest(this.config.selectors.variantInput);
			if (!input) return;
			const dialog = input.closest(this.config.selectors.dialog);
			if (!dialog) return;
			this._onVariantChange(dialog);
		});

		document.addEventListener('cart:add-to-cart-success', () => {
			const openDialog = document.querySelector(`${this.config.selectors.dialog}.is-open`);
			if (openDialog && window.MmPanel) window.MmPanel.close(openDialog.id);
		});
	},

	_findTemplate(cardId) {
		return document.querySelector(`${this.config.selectors.template}[data-card-id="${cardId}"]`);
	},

	_fetchTemplate(trigger) {
		const cardId = trigger.dataset.cardId;
		const url = trigger.dataset.quickAddUrl;
		if (!url) return Promise.resolve();
		if (!this._requests.has(cardId)) {
			const separator = url.includes('?') ? '&' : '?';
			const request = fetch(`${url}${separator}section_id=mm-quick-add`)
				.then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
				.then((html) => {
					if (this._findTemplate(cardId)) return;
					const doc = new DOMParser().parseFromString(html, 'text/html');
					const template = doc.querySelector(`${this.config.selectors.template}[data-card-id="${cardId}"]`);
					if (template) document.body.appendChild(document.importNode(template, true));
				})
				.catch((error) => {
					this._requests.delete(cardId);
					window.log('error', '🔧 [QUICK_ADD]', 'Quick-add fetch failed', { cardId, error });
				});
			this._requests.set(cardId, request);
		}
		return this._requests.get(cardId);
	},

	_hydrate(cardId) {
		if (!cardId || this._hydrated.has(cardId)) return;

		const template = this._findTemplate(cardId);
		if (!template) {
			window.log('error', '🔧 [QUICK_ADD]', 'Quick-add template not found', { cardId });
			return;
		}

		const fragment = template.content.cloneNode(true);
		document.body.appendChild(fragment);
		this._hydrated.add(cardId);

		// mm-size-selector.js (.mm-dd) et mm-product-cta.js (rotation de
		// réassurance, .mm-pcta) ne s'initialisent qu'au chargement de la page
		// — jamais pour un contenu cloné depuis un <template>. Les deux
		// exposent un point d'entrée dédié à l'injection tardive.
		const dialog = document.getElementById(`QuickAdd-${cardId}`);
		if (!dialog) return;
		if (window.MmSizeSelector) window.MmSizeSelector.init(dialog);
		if (window.MmProductCta) window.MmProductCta.setup(dialog);
	},

	_mountMedia(dialog) {
		const mediaRoot = dialog.querySelector(this.config.selectors.mediaRoot);
		if (!mediaRoot || mediaRoot.classList.contains(this.config.classes.mounted)) return;
		if (typeof Splide === 'undefined') return;

		const sliderId = mediaRoot.getAttribute('data-quick-add-media-id');
		const splideEl = sliderId ? document.getElementById(sliderId) : null;
		if (!splideEl) return;

		try {
			const splide = new Splide(splideEl, {
				arrows: false,
				pagination: false,
				rewind: true,
				breakpoints: {
					768: { arrows: true },
				},
			}).mount();
			this._sliders.set(sliderId, splide);
			mediaRoot.classList.add(this.config.classes.mounted);
		} catch (error) {
			window.log('error', '🔧 [QUICK_ADD]', 'Failed to mount media slider', { error: error.message });
		}
	},

	_onThumbClick(thumb) {
		const mediaRoot = thumb.closest(this.config.selectors.mediaRoot);
		if (!mediaRoot) return;
		const sliderId = mediaRoot.getAttribute('data-quick-add-media-id');
		const splide = this._sliders.get(sliderId);
		if (!splide) return;

		const index = parseInt(thumb.getAttribute('data-quick-add-media-thumb'), 10);
		if (Number.isNaN(index)) return;
		splide.go(index);

		mediaRoot.querySelectorAll(this.config.selectors.mediaThumb).forEach((el) => {
			el.classList.toggle(this.config.classes.thumbActive, el === thumb);
		});
	},

	_getVariantsData(dialog) {
		try {
			const variantsScript = dialog.querySelector(this.config.selectors.variantsJson);
			const pricesScript = dialog.querySelector(this.config.selectors.pricesJson);
			if (!variantsScript) return null;
			return {
				variants: JSON.parse(variantsScript.textContent),
				prices: pricesScript ? JSON.parse(pricesScript.textContent) : {},
			};
		} catch (error) {
			window.log('error', '🔧 [QUICK_ADD]', 'Failed to parse variants JSON', { error: error.message });
			return null;
		}
	},

	_findMatchingVariant(dialog, variants) {
		const selected = [];
		dialog.querySelectorAll(`${this.config.selectors.variantInput}:checked`).forEach((input) => {
			selected.push(input.value);
		});
		if (!selected.length) return null;

		// variant.options est un tableau ordonné (option1/2/3) : une variante
		// correspond à la sélection si elle contient exactement les mêmes
		// valeurs, indépendamment de l'ordre dans lequel les inputs ont été
		// cochés dans le DOM.
		return variants.find((variant) => {
			if (!Array.isArray(variant.options) || variant.options.length !== selected.length) return false;
			return selected.every((value) => variant.options.includes(value));
		});
	},

	_onVariantChange(dialog) {
		const data = this._getVariantsData(dialog);
		if (!data) return;

		const variant = this._findMatchingVariant(dialog, data.variants);
		if (!variant) {
			window.log('warn', '🔧 [QUICK_ADD]', 'No matching variant for current selection');
			return;
		}

		this._updateHiddenInput(dialog, variant);
		this._updatePrice(dialog, variant, data.prices[variant.id]);
		this._updateMedia(dialog, variant);
		this._updateCta(dialog, variant);
		this._updateVariantAvailability(dialog, data.variants);
	},

	_updateHiddenInput(dialog, variant) {
		const input = dialog.querySelector(this.config.selectors.hiddenIdInput);
		if (input) input.value = variant.id;
	},

	_updatePrice(dialog, variant, priceInfo) {
		if (!priceInfo) return;
		const priceEl = dialog.querySelector(this.config.selectors.priceCurrent);
		if (priceEl) {
			priceEl.textContent = priceInfo.price_formatted;
			priceEl.setAttribute('data-price', variant.price);
		}
		const ctaPriceEl = dialog.querySelector(this.config.selectors.ctaPrice);
		if (ctaPriceEl) ctaPriceEl.textContent = priceInfo.price_formatted;
		const compareEl = dialog.querySelector(this.config.selectors.priceCompare);
		const compareValueEl = dialog.querySelector(this.config.selectors.priceCompareValue);
		if (compareEl && compareValueEl && priceInfo.has_compare) {
			compareEl.hidden = false;
			compareValueEl.textContent = priceInfo.compare_formatted;
		} else if (compareEl) {
			compareEl.hidden = true;
		}

		const percentageEl = dialog.querySelector(this.config.selectors.pricePercentage);
		const percentageTextEl = dialog.querySelector(this.config.selectors.pricePercentageText);
		if (percentageEl && percentageTextEl && priceInfo.has_compare) {
			percentageEl.hidden = false;
			percentageTextEl.textContent = priceInfo.percentage_text;
		} else if (percentageEl) {
			percentageEl.hidden = true;
		}

		const savingEl = dialog.querySelector(this.config.selectors.priceSaving);
		const savingTextEl = dialog.querySelector(this.config.selectors.priceSavingText);
		if (savingEl && savingTextEl && priceInfo.has_saving) {
			savingEl.hidden = false;
			savingTextEl.textContent = priceInfo.saving_text;
		} else if (savingEl) {
			savingEl.hidden = true;
		}
	},

	_updateMedia(dialog, variant) {
		if (!variant.featured_media) return;
		const mediaRoot = dialog.querySelector(this.config.selectors.mediaRoot);
		if (!mediaRoot) return;

		const sliderId = mediaRoot.getAttribute('data-quick-add-media-id');
		const splide = this._sliders.get(sliderId);
		if (!splide) return;

		const slides = mediaRoot.querySelectorAll('[data-media-id]');
		const targetIndex = Array.from(slides).findIndex((slide) => {
			return parseInt(slide.dataset.mediaId, 10) === variant.featured_media.id;
		});

		if (targetIndex === -1) return;
		splide.go(targetIndex);

		mediaRoot.querySelectorAll(this.config.selectors.mediaThumb).forEach((el, index) => {
			el.classList.toggle(this.config.classes.thumbActive, index === targetIndex);
		});
	},

	_updateCta(dialog, variant) {
		const ctaRoot = dialog.querySelector(this.config.selectors.ctaRoot);
		if (!ctaRoot) return;

		// mm-product-cta.liquid rend les deux états (CTA panier + lien
		// pcta_oos_link) côté serveur quand pcta_oos_link est fourni : ce JS
		// local ne peut pas re-render le HTML comme le fait
		// ProductManager.updateReloadElements sur la PDP (Section Rendering
		// API), donc on bascule juste leur visibilité au lieu de désactiver
		// un bouton qui resterait affiché à tort.
		const oosState = ctaRoot.querySelector(this.config.selectors.oosState);
		const availableState = ctaRoot.querySelector(this.config.selectors.availableState);
		if (oosState && availableState) {
			oosState.hidden = variant.available;
			availableState.hidden = !variant.available;

			// variant.url (natif Shopify, présent dans le JSON product.variants)
			// pointe déjà vers /products/handle?variant=<id> : le client
			// atterrit sur la PDP avec la variante souhaitée déjà sélectionnée
			// au lieu de devoir la re-choisir avant d'ouvrir le trigger Klaviyo.
			const oosLink = ctaRoot.querySelector(this.config.selectors.oosLink);
			if (oosLink && variant.url) oosLink.href = variant.url;
			return;
		}

		const submitButton = ctaRoot.querySelector(this.config.selectors.submitButton);
		if (submitButton) submitButton.disabled = !variant.available;
	},

	// Après changement de variante, chaque valeur des autres sélecteurs
	// (photo-grid, pills) doit refléter sa propre disponibilité — combinée
	// aux valeurs actuellement sélectionnées sur les AUTRES options, même
	// logique que le matching Liquid de mm-product-variant-photo-grid /
	// mm-product-variant-pills. Sur la PDP c'est
	// ProductManager.updateReloadElements qui s'en charge via un re-render
	// serveur ; ici tout est local.
	_updateVariantAvailability(dialog, variants) {
		const selectedByOption = new Map();
		dialog.querySelectorAll(`${this.config.selectors.variantInput}:checked`).forEach((input) => {
			const match = input.name.match(/^options\[(.+)\]$/);
			if (!match) return;
			selectedByOption.set(match[1], input.value);
		});

		dialog.querySelectorAll(this.config.selectors.variantValue).forEach((wrapper) => {
			const input = wrapper.querySelector(this.config.selectors.variantInput);
			const option = wrapper.querySelector(`${this.config.selectors.photoOption}, ${this.config.selectors.pillOption}`);
			if (!input || !option) return;

			const match = input.name.match(/^options\[(.+)\]$/);
			if (!match) return;
			const optionName = match[1];

			const candidate = new Map(selectedByOption);
			candidate.set(optionName, input.value);
			const candidateValues = Array.from(candidate.values());

			const matched = variants.find((variant) => {
				if (!Array.isArray(variant.options) || variant.options.length !== candidateValues.length) return false;
				return candidateValues.every((value) => variant.options.includes(value));
			});

			const isOos = matched ? !matched.available : false;
			option.classList.toggle(this.config.classes.photoOos, isOos && option.matches(this.config.selectors.photoOption));
			option.classList.toggle(this.config.classes.pillOos, isOos && option.matches(this.config.selectors.pillOption));
			const oosLabel = option.querySelector('[data-variant-oos-label]');
			if (oosLabel) oosLabel.hidden = !isOos;
		});
	},
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => QuickAddManager.init());
} else {
	QuickAddManager.init();
}
